import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import Alert from "@material-ui/lab/Alert";
import SettingsIcon from "@material-ui/icons/Settings";

import { AccountAlert } from "../../interfaces";
import { Panel, PanelHeader } from "../Panel";
import { LocalizedSelect, FieldWrapper, LocalizedCheckbox } from "../Input";
import { LocalizedButton } from "../Button";
import { LocalizedText } from "../LocalizedText";
import { SkeletonForm } from "../SkeletonForm";

import { AlertSettingsViewProps } from "./interfaces";
import { useStyles } from "./styles";
import { useTranslation } from "react-i18next";

/**
 * Validation rules for the form
 */
const schema = yup.object({
  events: yup.array().of(yup.string()),
  recipients: yup.array().of(yup.string()),
  sendEmailNotification: yup.boolean(),
  sendPushNotification: yup.boolean(),
});

/**
 * A component to allow updating alert settings for an account
 */
export const AlertSettingsView: React.FC<AlertSettingsViewProps> = ({
  values,
  users,
  filteredEvents,
  loading,
  error,
  success,
  isSubmitting,
  onSubmit,
  accountId,
}) => {
  const classes = useStyles();
  const [showSkeleton, setShowSkeleton] = React.useState(loading);
  const { t } = useTranslation();

  const formik = useFormik<AccountAlert>({
    initialValues: {
      events: [],
      recipients: [],
      sendEmailNotification: true,
      sendPushNotification: true,
    },
    validateOnBlur: true,
    validationSchema: schema,
    onSubmit: (values) => {
      if (!accountId) return;
      onSubmit(accountId, values);
    },
  });

  /**
   * Once data has loaded, fill the form
   */
  React.useEffect(() => {
    if (!values) return;

    formik.setValues(values);

    setShowSkeleton(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formik changes each render so cannot be declared as a dependency
  }, [values]);

  const header = (
    <PanelHeader
      icon={<SettingsIcon />}
      title={<LocalizedText text="alert_settings" />}
    ></PanelHeader>
  );

  const noAlertSentWarning =
    values?.events?.length === 0 ||
    values?.recipients?.length === 0 ||
    values?.sendEmailNotification === false;

  return (
    <Panel header={header}>
      {error && (
        <Alert className={classes.alert} severity="error">
          <LocalizedText text="an_error_occurred" />
        </Alert>
      )}
      {success && (
        <Alert className={classes.alert} severity="info">
          <LocalizedText text="changes_saved" />
        </Alert>
      )}
      {noAlertSentWarning && (
        <Alert className={classes.alert} severity="warning">
          <LocalizedText text="no_alert_sent_warning" />
        </Alert>
      )}

      {showSkeleton ? (
        <SkeletonForm numFields={3} />
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <FieldWrapper>
            <LocalizedSelect
              fullWidth
              label="events"
              id="events"
              name="events"
              value={formik.values.events}
              onChange={formik.handleChange}
              error={formik.touched.events && Boolean(formik.errors.events)}
              helperText={
                formik.touched.events
                  ? (formik.errors.events as string)
                  : "events_help"
              }
              options={(filteredEvents || []).map((event) => ({
                label: t(event.label),
                value: event.value.toUpperCase(),
              }))}
              multiple
            />
          </FieldWrapper>

          <FieldWrapper>
            <LocalizedSelect
              fullWidth
              label="recipients"
              id="recipients"
              name="recipients"
              value={formik.values.recipients}
              onChange={formik.handleChange}
              error={
                formik.touched.recipients && Boolean(formik.errors.recipients)
              }
              helperText={
                formik.touched.recipients
                  ? (formik.errors.recipients as string)
                  : "recipients_help"
              }
              options={
                users?.map((user) => ({
                  label: `${user.firstName} ${user.lastName} (${user.email})`,
                  value: user.email,
                })) || []
              }
              multiple
            />
          </FieldWrapper>

          <FieldWrapper>
            <LocalizedCheckbox
              label="send_email_notification"
              name="sendEmailNotification"
              onChange={formik.handleChange}
              checked={formik.values.sendEmailNotification}
              helperText="send_email_notification_help"
            />
          </FieldWrapper>

          <FieldWrapper>
            <LocalizedCheckbox
              label="send_push_notification"
              name="sendPushNotification"
              onChange={formik.handleChange}
              checked={formik.values.sendPushNotification}
              helperText="send_push_notification_help"
            />
          </FieldWrapper>

          <LocalizedButton fullWidth disabled={isSubmitting} type="submit">
            {isSubmitting ? "saving" : "save"}
          </LocalizedButton>
        </form>
      )}
    </Panel>
  );
};
